import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../../utils/index.ts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { Button } from "../ui/button.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import { Progress } from "../ui/progress.jsx";
import { Separator } from "../ui/separator.jsx";
import { 
  Eye, 
  Edit, 
  Trash2, 
  Pencil, 
  Calendar,
  Building2,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle2,
  PlayCircle,
  PauseCircle,
  MoreHorizontal,
  ArrowRight,
  Star,
  TrendingUp
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.jsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip.jsx";
import { motion } from 'framer-motion';

const STATUS_COLORS = {
  planning: 'bg-purple-100 text-purple-800 border-purple-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  paused: 'bg-orange-100 text-orange-800 border-orange-300',
  completed: 'bg-green-100 text-green-800 border-green-300'
};

const STATUS_LABELS = {
  planning: 'Planejamento',
  in_progress: 'Em Andamento',
  paused: 'Pausado',
  completed: 'Concluído'
};

const STATUS_ICONS = {
  planning: <Clock className="w-4 h-4" />,
  in_progress: <PlayCircle className="w-4 h-4" />,
  paused: <PauseCircle className="w-4 h-4" />,
  completed: <CheckCircle2 className="w-4 h-4" />
};

export default function ProjectsList({ projects, companies = [], isLoading, onViewProject, navigate, onDelete }) {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  const getCompanyName = (companyId) => {
    if (!companyId || companyId === "1" || !companies || !Array.isArray(companies)) {
      return "Cliente não encontrado";
    }
    
    const company = companies.find(c => c.id === companyId);
    return company ? (company.corporate_name || company.name) : 'Cliente não encontrado';
  };

  const calculateProgress = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'in_progress') return 60;
    if (project.status === 'planning') return 20;
    if (project.status === 'paused') return 40;
    return 0;
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTeamMembers = (project) => {
    if (!project.team || !Array.isArray(project.team)) return [];
    return project.team;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-8 w-24 bg-gray-200 rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (viewMode === 'cards') {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {projects && projects.length > 0 ? (
          projects.map((project, index) => {
            const progress = calculateProgress(project);
            const daysRemaining = getDaysRemaining(project.end_date);
            const teamMembers = getTeamMembers(project);
            
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-200 group">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-2 group-hover:text-primary transition-colors">
                          {project.name}
                        </CardTitle>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building2 className="w-4 h-4" />
                          <span>{getCompanyName(project.client_id)}</span>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onViewProject(project.id)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Visualizar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(createPageUrl(`EditProject?id=${project.id}`))}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {onDelete && (
                            <DropdownMenuItem 
                              onClick={() => onDelete(project.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge className={STATUS_COLORS[project.status]} variant="outline">
                        {STATUS_ICONS[project.status]}
                        <span className="ml-1">{STATUS_LABELS[project.status]}</span>
                      </Badge>
                      {daysRemaining !== null && (
                        <div className={`text-sm font-medium ${
                          daysRemaining < 0 ? 'text-red-600' : 
                          daysRemaining <= 7 ? 'text-orange-600' : 'text-green-600'
                        }`}>
                          {daysRemaining < 0 ? 
                            `${Math.abs(daysRemaining)} dias atrasado` :
                            `${daysRemaining} dias restantes`
                          }
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progresso</span>
                        <span className="font-medium">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <div>
                          <div className="font-medium text-foreground">Início</div>
                          <div>
                            {project.start_date ? 
                              format(new Date(project.start_date), "dd/MM/yy", { locale: ptBR }) : 
                              'Não definido'
                            }
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <div>
                          <div className="font-medium text-foreground">Término</div>
                          <div>
                            {project.end_date ? 
                              format(new Date(project.end_date), "dd/MM/yy", { locale: ptBR }) : 
                              'Não definido'
                            }
                          </div>
                        </div>
                      </div>
                    </div>

                    {teamMembers.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Equipe</span>
                          <span className="text-xs text-muted-foreground">{teamMembers.length} membros</span>
                        </div>
                        <div className="flex -space-x-2">
                          {teamMembers.slice(0, 4).map((member, i) => (
                            <Tooltip key={i}>
                              <TooltipTrigger asChild>
                                <Avatar className="h-8 w-8 border-2 border-background">
                                  <AvatarFallback className="text-xs">
                                    {member.split('@')[0].substring(0, 2).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                              </TooltipTrigger>
                              <TooltipContent>{member}</TooltipContent>
                            </Tooltip>
                          ))}
                          {teamMembers.length > 4 && (
                            <div className="h-8 w-8 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium">
                              +{teamMembers.length - 4}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <Separator />
                    
                    <Button 
                      variant="ghost" 
                      className="w-full justify-between hover:bg-primary/5" 
                      onClick={() => onViewProject(project.id)}
                    >
                      Ver detalhes
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        ) : (
          <div className="col-span-full">
            <Card>
              <CardContent className="p-12 text-center">
                <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhum projeto encontrado</h3>
                <p className="text-muted-foreground">
                  Não há projetos que correspondam aos filtros aplicados.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Lista de Projetos</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              Tabela
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
            >
              Cards
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Projeto</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progresso</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Equipe</TableHead>
                <TableHead>Gestor</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects && projects.length > 0 ? (
                projects.map((project) => {
                  const progress = calculateProgress(project);
                  const daysRemaining = getDaysRemaining(project.end_date);
                  const teamMembers = getTeamMembers(project);
                  
                  return (
                    <TableRow 
                      key={project.id} 
                      className="hover:bg-muted/50 transition-colors"
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">{project.name}</div>
                          {project.description && (
                            <div className="text-sm text-muted-foreground truncate max-w-xs">
                              {project.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span>{getCompanyName(project.client_id)}</span>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <Badge className={STATUS_COLORS[project.status]} variant="outline">
                          {STATUS_ICONS[project.status]}
                          <span className="ml-1">{STATUS_LABELS[project.status]}</span>
                        </Badge>
                      </TableCell>
                      
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[120px]">
                          <Progress value={progress} className="flex-1 h-2" />
                          <span className="text-sm font-medium">{progress}%</span>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3" />
                            {project.start_date && format(new Date(project.start_date), "dd/MM/yy", { locale: ptBR })}
                            {project.end_date && (
                              <> - {format(new Date(project.end_date), "dd/MM/yy", { locale: ptBR })}</>
                            )}
                          </div>
                          {daysRemaining !== null && (
                            <div className={`text-xs font-medium ${
                              daysRemaining < 0 ? 'text-red-600' : 
                              daysRemaining <= 7 ? 'text-orange-600' : 'text-green-600'
                            }`}>
                              {daysRemaining < 0 ? 
                                `${Math.abs(daysRemaining)} dias atrasado` :
                                `${daysRemaining} dias restantes`
                              }
                            </div>
                          )}
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        {teamMembers.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{teamMembers.length}</span>
                            <div className="flex -space-x-1 ml-1">
                              {teamMembers.slice(0, 3).map((member, i) => (
                                <Tooltip key={i}>
                                  <TooltipTrigger asChild>
                                    <Avatar className="h-6 w-6 border border-background">
                                      <AvatarFallback className="text-[10px]">
                                        {member.split('@')[0].substring(0, 2).toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                  </TooltipTrigger>
                                  <TooltipContent>{member}</TooltipContent>
                                </Tooltip>
                              ))}
                              {teamMembers.length > 3 && (
                                <div className="h-6 w-6 rounded-full bg-muted border border-background flex items-center justify-center text-[10px] font-medium">
                                  +{teamMembers.length - 3}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <span className="text-sm">
                          {project.manager || '-'}
                        </span>
                      </TableCell>
                      
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onViewProject(project.id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Visualizar</TooltipContent>
                          </Tooltip>
                          
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => navigate(createPageUrl(`EditProject?id=${project.id}`))}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Editar</TooltipContent>
                          </Tooltip>
                          
                          {onDelete && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-600"
                                  onClick={() => onDelete(project.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Excluir</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Building2 className="w-8 h-8 text-muted-foreground" />
                      <span>Nenhum projeto encontrado.</span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}